import { AfterContentInit, ChangeDetectorRef, Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTableModule } from 'ng-zorro-antd/table';

import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';

import { finalize } from 'rxjs/operators';

import { NzDrawerRef } from 'ng-zorro-antd/drawer';

import { AlertsInterface } from '../../../core/models';
import { ErrorsHandlingService } from '../../../core/services';
import { AlertsService } from '../../../modules/alerts/services';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
@Component({
  selector: 'app-alerts-drawer',
  templateUrl: './alerts-drawer.component.html',
  styleUrls: ['./alerts-drawer.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzDrawerModule,
    NzDividerModule,
    NzButtonModule,
    NzInputModule,
    NzIconModule,
    NzTableModule,
    NzSelectModule,
    NzSpinModule,
  ]
})
export class AlertsDrawerComponent implements OnInit, AfterContentInit {
  @Input() data: {
    alertsCount: number;
  };

  public isLoading = false;

  public drawerMaxHeight = 'auto';
  public drawerBodyMaxHeight = 'auto';
  public drawerContentMaxHeight = 'auto';

  public set alertsCount(value: number) {
    this._alertsCount = value;
  }

  public get alertsCount(): number {
    return this._alertsCount;
  }

  public set alertsArray(value: AlertsInterface.IAlert[]) {
    this._alertsArray = value;
  }

  public get alertsArray(): AlertsInterface.IAlert[] {
    return this._alertsArray;
  }

  private _alertsCount: number;
  private _alertsArray: AlertsInterface.IAlert[] = [];

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private drawerRef: NzDrawerRef<AlertsDrawerComponent>,
    private router: Router,
    private alertsService: AlertsService,
    private errorsHandlingService: ErrorsHandlingService,
    private cd: ChangeDetectorRef,
  ) { }

  ngAfterContentInit(): void {
    this.drawerMaxHeight = window.innerHeight - 10 + 'px';
    this.drawerBodyMaxHeight = window.innerHeight - 50 + 'px';
    this.drawerContentMaxHeight = window.innerHeight - 100 + 'px';
    window.addEventListener('resize', () => {
      this.drawerMaxHeight = window.innerHeight - 10 + 'px';
      this.drawerBodyMaxHeight = window.innerHeight - 50 + 'px';
      this.drawerContentMaxHeight = window.innerHeight - 100 + 'px';
    });
  }

  ngOnInit(): void {
    this.alertsCount = this.data.alertsCount;

    // The badge count is kept up to date by AlertsService, including the local decrement it does
    // right after an alert is marked as read, so the title follows it without another request.
    this.alertsService.alertsObs
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(count => this.alertsCount = count);

    // The unread list is kept warm by the layout's periodic poll, so the drawer renders it from
    // this shared cache instead of firing its own request every time it opens.
    this.alertsService.bellAlertsObs
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(alerts => {
        this.alertsArray = alerts;
        this.cd.markForCheck();
      });
  }

  public readAllAlerts(): void {
    this.alertsService.readAllAlerts(() => this.refreshAlerts());
  }

  public readAlert(alertId: number): void {
    this.alertsService.readAlert(alertId, () => this.refreshAlerts());
  }

  public showAllAlerts(): void {
    this.setUserActionCount(1);
    this.router.navigate(['alerts']);
    this.drawerRef.close();
  }

  // Marking an alert read flips is_read on the server, so re-pull the unread list to refresh both
  // the drawer and the badge. This is the only request the drawer makes — opening it reuses the
  // cache the layout already keeps warm.
  private refreshAlerts(): void {
    this.isLoading = true;

    this.alertsService.refreshBellAlerts()
      .pipe(finalize(() => {
        this.isLoading = false;
        this.cd.markForCheck();
      }))
      .subscribe(() => { },
        error => this.errorsHandlingService.handleError(error));
  }

  private setUserActionCount(value: number): void {
    this.alertsService.setUserActionCount(value).subscribe(() => { },
      error => this.errorsHandlingService.handleError(error));
  }
}
